import React, {useCallback, useEffect, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Button, Space, Tabs, message} from 'antd';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {HasResource} from '../../../components/HasResource';
import {ResourceCode} from '../../../constants/resourceCodes';
import TablePaginationBar from '../../../components/TablePaginationBar';
import PopReviewDetailDrawer from './components/PopReviewDetailDrawer';
import PopReviewFilter from './components/PopReviewFilter';
import PopReviewTable from './components/PopReviewTable';
import {
    GET_POP_REVIEW_COUNT,
    GetPopReviewCountResponse
} from './graphql/queries/getPopReviewCount';
import {PopReviewFilters, PopReviewTabType, PopReviewType} from './graphql/types/PopReviewType';
import {usePopReviewOptions} from './hooks/usePopReviewOptions';
import {buildFiltersInputFromFilters, usePopReviewQuery} from './hooks/usePopReviewQuery';

/**
 * 默认筛选：提交时间必填，默认最近一个月。
 * 必填是性能兜底 —— 列表主查询按 submitted_at 排序，无时间条件的全量扫描在数据量上来后很危险。
 */
const getDefaultFilters = (): PopReviewFilters => ({
    submittedAt: [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')]
});

/**
 * 揽收 POP 审核工作台
 *
 * Tab 与「审核状态」筛选以 AND 组合，二者矛盾时（如新标记 + 合格）返回空结果。
 * 批量审核为纯前端交互：Drawer 内逐条审、审完跳下一条，服务端只提供单条接口。
 */
const PopReviewPage: React.FC = () => {
    const {t} = useTranslation();

    const [activeTab, setActiveTab] = useState<PopReviewTabType>(PopReviewTabType.NEW);
    const [tempFilters, setTempFilters] = useState<PopReviewFilters>(getDefaultFilters());
    const [filters, setFilters] = useState<PopReviewFilters>(getDefaultFilters());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<PopReviewType[]>([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [reviewIds, setReviewIds] = useState<string[]>([]);

    const {popReviewData, loading, executeQuery, totalElements} = usePopReviewQuery();
    // 传 tempFilters.pickupHubId：站点下拉随用户当前选择即时联动，不等点「查询」
    const {
        reasonOptions,
        reasonTextByCode,
        disqualifyReasons,
        hubNameById,
        pickupSites,
        pickupDrivers,
        pickupSiteNameById,
        pickupDriverNameById,
        loading: optionsLoading
    } = usePopReviewOptions(tempFilters.pickupHubId);

    const pickupSiteOptions = pickupSites.map(site => ({value: site.id, label: site.name}));
    const pickupDriverOptions = pickupDrivers.map(driver => ({
        value: driver.id,
        label: driver.name
    }));

    // 只为非当前 Tab 发计数请求：当前 Tab 的计数与列表 totalElements 是同一个数，
    // 复用它可省掉首屏 1 次请求。用已提交的 filters 而非 tempFilters，与列表保持同源。
    const inactiveTab =
        activeTab === PopReviewTabType.NEW ? PopReviewTabType.TERMINATED : PopReviewTabType.NEW;
    const {
        data: inactiveCountData,
        loading: inactiveCountLoading,
        refetch: refetchInactiveCount
    } = useQuery<GetPopReviewCountResponse>(GET_POP_REVIEW_COUNT, {
        variables: {filters: buildFiltersInputFromFilters(filters, inactiveTab)},
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
    });

    const refetchCounts = useCallback(() => {
        void refetchInactiveCount();
    }, [refetchInactiveCount]);

    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const reload = useCallback(
        (nextFilters: PopReviewFilters, page: number, size: number, tab: PopReviewTabType) => {
            executeQuery(nextFilters, page, size, tab);
            clearSelection();
        },
        [executeQuery]
    );

    // 首屏与 Tab 切换时回到第一页重查
    useEffect(() => {
        setCurrentPage(1);
        reload(filters, 1, pageSize, activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleSearch = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
        reload(tempFilters, 1, pageSize, activeTab);
    };

    const handleReset = () => {
        const defaults = getDefaultFilters();
        setTempFilters(defaults);
        setFilters(defaults);
        setCurrentPage(1);
        reload(defaults, 1, pageSize, activeTab);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
        reload(filters, page, size, activeTab);
    };

    const showDetail = (record: PopReviewType) => {
        setReviewIds([record.reviewTaskId]);
        setDrawerVisible(true);
    };

    const handleBatchReview = () => {
        if (selectedRows.length === 0) {
            message.warning(t('popReview.messages.selectAtLeastOne'));
            return;
        }
        setReviewIds(selectedRows.map(row => row.reviewTaskId));
        setDrawerVisible(true);
    };

    // 单条审完即刷新，不等 Drawer 关闭：reload 刷新当前 Tab（即列表 totalElements），
    // refetchCounts 刷新另一个 Tab
    const handleReviewed = () => {
        reload(filters, currentPage, pageSize, activeTab);
        refetchCounts();
    };

    // Tab 角标：计数加载中显示 '...'，避免闪 0 造成「真的没有数据」的误读
    const getTabLabel = (labelKey: string, count?: number, isLoading?: boolean) =>
        `${t(labelKey)} (${isLoading || count == null ? '...' : count})`;

    /**
     * 当前 Tab 用列表的 totalElements，非当前 Tab 用独立计数查询。
     * 当前 Tab 必须以列表 loading 为准：切 Tab 后新列表返回前 totalElements 仍是旧值，
     * 直接显示会短暂错位。
     */
    const getCountProps = (tab: PopReviewTabType): [number | undefined, boolean] =>
        tab === activeTab
            ? [totalElements, loading]
            : [inactiveCountData?.getPopReviewCount, inactiveCountLoading];

    const tabs = [
        {
            key: PopReviewTabType.NEW,
            label: getTabLabel('popReview.tab.new', ...getCountProps(PopReviewTabType.NEW))
        },
        {
            key: PopReviewTabType.TERMINATED,
            label: getTabLabel(
                'popReview.tab.terminated',
                ...getCountProps(PopReviewTabType.TERMINATED)
            )
        }
    ];

    return (
        <div className="p-4">
            <PopReviewFilter
                filters={tempFilters}
                onChange={setTempFilters}
                reasonOptions={reasonOptions}
                pickupSiteOptions={pickupSiteOptions}
                pickupDriverOptions={pickupDriverOptions}
            />

            {/*
                Tab 与按钮合并为一行：POP 只有 2 个短 Tab，分两行会留下大片空白。
                Tab 语义是对筛选结果的再分类，非页面级模式切换，故切 Tab 保留筛选条件。
            */}
            <div className="flex justify-between items-center mb-3">
                <Tabs
                    activeKey={activeTab}
                    onChange={key => setActiveTab(key as PopReviewTabType)}
                    items={tabs.map(tab => ({key: tab.key, label: tab.label}))}
                    className="[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-tab]:font-bold"
                />
                <Space>
                    {activeTab === PopReviewTabType.NEW && (
                        <HasResource resourceCodes={[ResourceCode.cs.CS_POP_REVIEW_OPERATE]}>
                            <Button
                                type="primary"
                                onClick={handleBatchReview}
                                disabled={selectedRows.length === 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                    {t('popReview.action.batchReview', {count: selectedRows.length})}
                                </span>
                            </Button>
                        </HasResource>
                    )}
                    <Button
                        type="primary"
                        loading={loading || optionsLoading}
                        onClick={handleSearch}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('popReview.action.filter')}</span>
                    </Button>
                    <Button onClick={handleReset} className="w-auto min-w-24">
                        <span className="font-bold">{t('common.reset')}</span>
                    </Button>
                </Space>
            </div>

            <div className="mb-4">
                <PopReviewTable
                    popReviewData={popReviewData}
                    loading={loading}
                    showDetail={showDetail}
                    onSelectChange={(keys, rows) => {
                        setSelectedRowKeys(keys);
                        setSelectedRows(rows);
                    }}
                    selectedRowKeys={selectedRowKeys}
                    hubNameById={hubNameById}
                    pickupSiteNameById={pickupSiteNameById}
                    pickupDriverNameById={pickupDriverNameById}
                    showRowSelection={activeTab === PopReviewTabType.NEW}
                />
            </div>

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />

            <PopReviewDetailDrawer
                open={drawerVisible}
                onClose={() => {
                    // 对齐 POD：关闭时清 reviewIds 与选中态并刷新列表，
                    // 否则列表行仍处选中态，再点「批量审核」会带出已审过的最后一条
                    setDrawerVisible(false);
                    setReviewIds([]);
                    // reload 内含 clearSelection（清 selectedRowKeys/selectedRows）+ executeQuery
                    reload(filters, currentPage, pageSize, activeTab);
                    refetchCounts();
                }}
                reviewIds={reviewIds}
                onReviewed={handleReviewed}
                reasonOptions={reasonOptions}
                reasonTextByCode={reasonTextByCode}
                disqualifyReasons={disqualifyReasons}
                hubNameById={hubNameById}
                pickupSiteNameById={pickupSiteNameById}
                pickupDriverNameById={pickupDriverNameById}
            />
        </div>
    );
};

export default PopReviewPage;
